import DocumentTemplatesTab from "./settings/DocumentTemplatesTab";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { AsyncButton } from "../components/AsyncButton";
import { LoadingOverlay } from "../components/LoadingOverlay";
import ClientForm, { BLANK_CLIENT_FORM, clientValuesToFormData, type ClientFormValues } from "../components/ClientForm";
import React, { useState, useEffect, useRef } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useUrlSyncedTab } from "../hooks/useUrlSyncedTab";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import {
  getLinkedInAuthUrl, getLinkedInUserStatus, disconnectLinkedIn, getIntegrations,
  saveSmtp, testSmtp, getClients, createClient, updateClient, deleteClient,
  getGmailStatus, disconnectGmail, getBranding, getSettingsBranding, saveBranding, getBrandingLogoUrl,
  getGmailCredentialsStatus, uploadGmailCredentials, getGmailAuthUrl,
  getLocale, saveLocale, getCurrencies,
  getAiUsage, getBillingUsage, getBillingInvoices, getStripePlans, createStripeCheckoutSession, createStripePortalSession,
  getModuleCommerce, addStripeModule, removeStripeModule,
  regenerateAllCVs,
  getErrorMessage,
} from "../api";
import api from "../api";
import { Briefcase, Receipt, Banknote, PenLine, Camera, Target, type LucideIcon } from "lucide-react";
import { MODULE_LABELS, MODULE_COLORS, ModuleKey } from "../moduleMetadata";
import { useModules } from "../hooks/useModules";

const getSettings = () => api.get("/api/settings/").then(r => r.data);
const updateSettings = (data: any) => api.put("/api/settings/", data).then(r => r.data);
const pullModel = (model: string) => api.post("/api/settings/ollama/pull", { model }).then(r => r.data);

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";
type Tab = "ai" | "usage" | "modules" | "social" | "email" | "clients" | "branding" | "templates" | "locale";

const COUNTRY_LIST = [
  "Afghanistan","Albania","Algeria","Angola","Argentina","Armenia","Australia","Austria",
  "Azerbaijan","Bahrain","Bangladesh","Belarus","Belgium","Bolivia","Bosnia and Herzegovina",
  "Brazil","Bulgaria","Cambodia","Canada","Chile","China","Colombia","Costa Rica","Croatia",
  "Czech Republic","Denmark","Ecuador","Egypt","El Salvador","Estonia","Ethiopia","Finland",
  "France","Georgia","Germany","Ghana","Greece","Guatemala","Honduras","Hong Kong","Hungary",
  "India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Japan","Jordan","Kazakhstan",
  "Kenya","Kuwait","Latvia","Lebanon","Libya","Lithuania","Luxembourg","Malaysia","Mexico",
  "Morocco","Myanmar","Nepal","Netherlands","New Zealand","Nigeria","Norway","Oman","Pakistan",
  "Palestine","Panama","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania",
  "Russia","Saudi Arabia","Serbia","Singapore","Slovakia","Slovenia","South Africa","South Korea",
  "Spain","Sri Lanka","Sweden","Switzerland","Taiwan","Tanzania","Thailand","Tunisia","Turkey",
  "Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay",
  "Uzbekistan","Venezuela","Vietnam","Yemen","Zimbabwe",
];

const TIMEZONE_LIST = [
  { value: "UTC",                    label: "UTC (UTC+00:00)" },
  { value: "Europe/London",          label: "London (UTC+00:00 / +01:00 BST)" },
  { value: "Europe/Paris",           label: "Paris / Berlin / Amsterdam (UTC+01:00)" },
  { value: "Europe/Helsinki",        label: "Helsinki / Kyiv (UTC+02:00)" },
  { value: "Europe/Moscow",          label: "Moscow (UTC+03:00)" },
  { value: "Asia/Dubai",             label: "Dubai / Abu Dhabi (UTC+04:00)" },
  { value: "Asia/Karachi",           label: "Karachi / Islamabad (UTC+05:00)" },
  { value: "Asia/Kolkata",           label: "India — IST (UTC+05:30)" },
  { value: "Asia/Kathmandu",         label: "Nepal (UTC+05:45)" },
  { value: "Asia/Dhaka",             label: "Dhaka / Almaty (UTC+06:00)" },
  { value: "Asia/Bangkok",           label: "Bangkok / Jakarta (UTC+07:00)" },
  { value: "Asia/Singapore",         label: "Singapore / Kuala Lumpur / Manila (UTC+08:00)" },
  { value: "Asia/Shanghai",          label: "China / Hong Kong / Taiwan (UTC+08:00)" },
  { value: "Asia/Tokyo",             label: "Tokyo / Seoul (UTC+09:00)" },
  { value: "Australia/Sydney",       label: "Sydney / Melbourne (UTC+10:00 / +11:00 AEDT)" },
  { value: "Pacific/Auckland",       label: "Auckland (UTC+12:00)" },
  { value: "Atlantic/Cape_Verde",    label: "Cape Verde (UTC-01:00)" },
  { value: "America/Sao_Paulo",      label: "São Paulo / Brasília (UTC-03:00)" },
  { value: "America/Halifax",        label: "Halifax — Atlantic (UTC-04:00)" },
  { value: "America/New_York",       label: "New York / Toronto — EST (UTC-05:00)" },
  { value: "America/Chicago",        label: "Chicago — CST (UTC-06:00)" },
  { value: "America/Denver",         label: "Denver — MST (UTC-07:00)" },
  { value: "America/Los_Angeles",    label: "Los Angeles / Vancouver — PST (UTC-08:00)" },
  { value: "America/Anchorage",      label: "Alaska (UTC-09:00)" },
  { value: "Pacific/Honolulu",       label: "Hawaii (UTC-10:00)" },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin;
  const isAdmin = user?.role === "admin" || user?.role === "admin_assistant" || isSuperAdmin;
  const tabs: [Tab, string][] = [
    ...(isSuperAdmin ? [["ai", "AI Provider"] as [Tab, string]] : []),
    ...(isAdmin ? [["usage", "Usage & Plan"] as [Tab, string]] : []),
    ...(isAdmin ? [["modules", "Modules"] as [Tab, string]] : []),
    ["social", "Social Media"],
    ["email", "Email & SMTP"],
    ["clients", "Clients"],
    ["branding", "Branding"],
    ["templates", "Document Templates"],
    ["locale", "Currency & Locale"],
  ];
  const defaultTab: Tab = isSuperAdmin ? "ai" : isAdmin ? "usage" : "social";
  const validTabs = tabs.map(([id]) => id);
  const [tab, setTab] = useUrlSyncedTab<Tab>(validTabs, defaultTab);

  return (
    <div style={{ maxWidth: 860 }}>
      <div className="page-header">
        <div>
          <h1>Settings</h1>
          <p>Configure AI, social media, email, and branding for your workspace</p>
        </div>
      </div>

      <div className="tab-pill-bar" style={{ marginBottom: 20 }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} className={tab === id ? "active" : ""}>{label}</button>
        ))}
      </div>

      {tab === "ai"    && isSuperAdmin && <AiTab />}
      {tab === "usage" && isAdmin      && <UsagePlanTab />}
      {tab === "modules" && isAdmin    && <ModulesTab />}
      {tab === "social" && <SocialTab />}
      {tab === "email" && <EmailTab />}
      {tab === "clients" && <ClientsTab />}
      {tab === "branding" && <BrandingTab />}
      {tab === "templates" && <DocumentTemplatesTab />}
      {tab === "locale" && <LocaleTab />}
    </div>
  );
}

// ── Modules tab ───────────────────────────────────────────────────────────────

/** One line each, in the buyer's language. Mirrors the signup step. */
const MODULE_PITCH: Record<string, string> = {
  recruitment:        "Job posts, CV screening and interview scheduling",
  finance:            "Invoicing, quotations, vendor bills and Thai tax docs",
  payroll:            "Salary runs, payslips and statutory deductions",
  esign:              "Send documents for legally-binding signature",
  ai_expense_capture: "Photograph a receipt, get a filed expense",
  performance:        "Goals, reviews and 360 feedback",
};

/**
 * Same icon each module already wears on its own dashboard, so the row a buyer
 * clicks here and the product they land in look like the same thing.
 */
const MODULE_ICON: Record<string, LucideIcon> = {
  recruitment:        Briefcase,
  finance:            Receipt,
  payroll:            Banknote,
  esign:              PenLine,
  ai_expense_capture: Camera,
  performance:        Target,
};

/**
 * Small semantic status pill — 12px, one colour pair from the design system.
 * Takes the text as a prop rather than as children: this project's React
 * typings don't thread JSX children into function components (the same quirk
 * that rules out `key` as a prop on <Tile> in ProductSwitcher).
 */
function Pill({ bg, color, border, label }: {
  bg: string; color: string; border: string; label: string;
}) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 8px", borderRadius: 999,
      background: bg, color, border: `1px solid ${border}`,
      fontSize: 12, fontWeight: 600, lineHeight: 1.4, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function formatAccessUntil(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

function ModulesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({ queryKey: ["module-commerce"], queryFn: getModuleCommerce, retry: false });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["module-commerce"] });
    // The nav and route guards read entitlements from their own query — without
    // this, a module bought here stays invisible until a reload.
    qc.invalidateQueries({ queryKey: ["my-modules"] });
  };

  const addMut = useMutation({
    mutationFn: addStripeModule,
    onSuccess: (r: any) => {
      toast(r?.charged
        ? "Module added — your card has been charged for the rest of this period"
        : "Module restored — no further charge, you had already paid for this period", "success");
      refresh();
    },
    onError: (e: any) => toast(getErrorMessage(e, "Could not add the module"), "error"),
  });

  const removeMut = useMutation({
    mutationFn: removeStripeModule,
    onSuccess: (r: any) => {
      toast(`Removed — access continues until ${formatAccessUntil(r.access_until)}`, "success");
      refresh();
    },
    onError: (e: any) => toast(getErrorMessage(e, "Could not remove the module"), "error"),
  });

  if (isLoading) return <div style={{ color: "#64748b", fontSize: 14 }}>Loading modules…</div>;

  const modules: any[] = data?.modules ?? [];

  if (!data?.has_subscription) {
    return (
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ margin: "0 0 6px", fontSize: 18, color: "#0f172a" }}>Modules</h3>
        <p style={{ margin: 0, fontSize: 14, color: "#334155" }}>
          Start a plan first — modules are add-ons to a live subscription. See the{" "}
          <strong>Usage &amp; Plan</strong> tab.
        </p>
      </div>
    );
  }

  const active = modules.filter(m => m.owned).length;
  const total = data?.modules_total_usd ?? 0;
  const per = data?.billing_period === "annual" ? "/yr" : "/mo";

  return (
    <div>
      {/* Header: what you have and what it costs, as two facts rather than one
          long sentence. The billing mechanics move to a footnote under the list —
          they matter at the moment of clicking Add or Remove, not before it. */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap", marginBottom: 16,
      }}>
        <div>
          <h3 style={{ margin: "0 0 3px", fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
            Modules
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
            Add-ons to your plan. {active} of {modules.length} active.
          </p>
        </div>
        {total > 0 && (
          <div style={{ textAlign: "right" }}>
            <div style={{
              fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1,
              fontVariantNumeric: "tabular-nums",
            }}>
              ${total}
              <span style={{ fontSize: 13, fontWeight: 600, color: "#64748b" }}>{per}</span>
            </div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>on top of your plan</div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {modules.map(m => {
          const busy = addMut.isPending || removeMut.isPending;
          const color = MODULE_COLORS[m.key as ModuleKey] ?? "#64748b";
          const Icon = MODULE_ICON[m.key];
          return (
            <div
              key={m.key}
              className="card"
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px 16px", flexWrap: "wrap",
                borderColor: m.pending_removal ? "#fcd34d" : undefined,
                background: m.pending_removal ? "#fffbeb" : undefined,
              }}
            >
              {/* Tinted tile in the module's identity colour — the same colour and
                  icon it wears in the launcher and on its own dashboard. The 8-digit
                  hex is the colour at ~8% alpha. */}
              <span style={{
                width: 38, height: 38, borderRadius: 9, flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `${color}14`, color,
              }}>
                {Icon ? <Icon size={18} strokeWidth={2} /> : null}
              </span>

              <div style={{ flex: 1, minWidth: 170 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                  marginBottom: 2,
                }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>
                    {MODULE_LABELS[m.key as ModuleKey] ?? m.key}
                  </span>
                  {m.pending_removal && m.access_until ? (
                    <Pill
                      bg="#fffbeb" color="#b45309" border="#fde68a"
                      label={`Ends ${formatAccessUntil(m.access_until)}`}
                    />
                  ) : m.owned ? (
                    // green-700, not the design system's #16a34a: at 12px that
                    // token is ~3.4:1 on its own wash, under WCAG AA for text.
                    <Pill bg="#f0fdf4" color="#15803d" border="#bbf7d0" label="Active" />
                  ) : null}
                </div>
                <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.45 }}>
                  {MODULE_PITCH[m.key] ?? ""}
                </div>
              </div>

              {/* Price and action travel together: on a phone they drop to their own
                  line as "price ……… [button]" rather than the price being squeezed
                  off the right edge by the description. */}
              <div className="module-row-meta">
              {m.price_usd != null && (
                <span className="module-row-price" style={{
                  fontSize: 15, fontWeight: 600, color: "#334155", whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}>
                  ${m.price_usd}
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#64748b" }}>{per}</span>
                </span>
              )}

              {/* Fixed-width action slot. Without it "Add", "Remove" and "Keep it"
                  set three different widths and the price column zig-zags. */}
              <div className="module-row-action">
              {m.pending_removal ? (
                <AsyncButton
                  className="btn-secondary"
                  disabled={busy}
                  onClick={async () => { await addMut.mutateAsync(m.key); }}
                >
                  Keep it
                </AsyncButton>
              ) : m.owned ? (
                <AsyncButton
                  className="btn-danger"
                  disabled={busy}
                  onClick={async () => {
                    const ok = await confirm(
                      "It will not be billed again. You keep access until the end of the period " +
                      "you have already paid for, and your data is kept either way.",
                      {
                        title: `Remove ${MODULE_LABELS[m.key as ModuleKey] ?? m.key}?`,
                        confirmLabel: "Remove",
                        danger: true,
                      },
                    );
                    if (ok) await removeMut.mutateAsync(m.key);
                  }}
                >
                  Remove
                </AsyncButton>
              ) : m.purchasable ? (
                <AsyncButton
                  className="btn-primary"
                  disabled={busy}
                  onClick={async () => {
                    const ok = await confirm(
                      "Your card will be charged for the remainder of this billing period, " +
                      "prorated. Access starts immediately.",
                      {
                        title: `Add ${MODULE_LABELS[m.key as ModuleKey] ?? m.key}?`,
                        confirmLabel: "Add module",
                      },
                    );
                    if (ok) await addMut.mutateAsync(m.key);
                  }}
                >
                  Add
                </AsyncButton>
              ) : (
                // No configured price — render no purchase control at all rather than
                // one that 400s (AC2).
                <span style={{ fontSize: 12, color: "#64748b" }}>Not available</span>
              )}
              </div>
              </div>
            </div>
          );
        })}
      </div>

      <p style={{
        margin: "14px 0 0", paddingTop: 12, borderTop: "1px solid #e2e8f0",
        fontSize: 12, color: "#64748b", lineHeight: 1.6,
      }}>
        Adding a module charges the rest of this period straight away. Removing one keeps
        access until the period you have paid for ends — your data is kept either way.
      </p>
    </div>
  );
}

// ── Merged Usage & Plan tab ───────────────────────────────────────────────────

const PLAN_LABEL: Record<string, string> = {
  trial: "Free Trial", starter: "Starter", growth: "Growth", pro: "Pro", enterprise: "Enterprise",
};
const STATUS_CHIP: Record<string, { bg: string; color: string; border: string }> = {
  active:   { bg: "#dcfce7", color: "#166534", border: "#86efac" },
  trialing: { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  grace:    { bg: "#fef9c3", color: "#854d0e", border: "#fde047" },
  readonly: { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" },
  expired:  { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
};
const FEATURE_LABELS: Record<string, string> = {
  chat: "AI Chat", cv_scoring: "CV Scoring", offer_letter: "Offer Letters",
  hr_letter: "HR Letters", job_description: "Job Descriptions",
};
const FEATURE_COLORS: Record<string, string> = {
  chat: "#7c3aed", cv_scoring: "#0891b2", offer_letter: "#1e3a8a",
  hr_letter: "#059669", job_description: "#d97706",
};

function UsagePlanTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const isMobile = useIsMobile(640);
  const [billingPeriod, setBillingPeriod] = React.useState<"monthly" | "annual">("monthly");

  const { data: billing, isLoading: billingLoading } = useQuery({
    queryKey: ["billing-usage"], queryFn: getBillingUsage,
    staleTime: 2 * 60 * 1000, retry: false,
  });
  const { data: ai, isLoading: aiLoading } = useQuery({
    queryKey: ["ai-usage"], queryFn: getAiUsage,
    staleTime: 2 * 60 * 1000, retry: false,
  });
  const { data: invoiceData } = useQuery({
    queryKey: ["billing-invoices"], queryFn: getBillingInvoices,
    staleTime: 2 * 60 * 1000, retry: false,
  });
  const { data: plansData, isLoading: plansLoading } = useQuery({
    queryKey: ["stripe-plans"], queryFn: getStripePlans,
    staleTime: 5 * 60 * 1000, retry: false,
  });

  const checkoutMut = useMutation({
    mutationFn: createStripeCheckoutSession,
    onSuccess: (data) => {
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      // Existing subscriber — plan was switched in place, no redirect needed.
      toast(`Switched to ${data.plan} plan`, "success");
      if (data.usage_warning) toast(data.usage_warning, "info");
      qc.invalidateQueries({ queryKey: ["billing-usage"] });
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to change plan", "error"),
  });

  const portalMut = useMutation({
    mutationFn: createStripePortalSession,
    onSuccess: (data) => { window.location.href = data.portal_url; },
    onError: (e: any) => toast(e?.response?.data?.detail || "Failed to open billing portal", "error"),
  });

  if (billingLoading || aiLoading || plansLoading) return <p style={{ color: "#64748b" }}>Loading…</p>;
  if (!billing) return <p style={{ color: "#64748b" }}>Unable to load billing info.</p>;

  const chip = STATUS_CHIP[billing.effective_status] || STATUS_CHIP.active;
  const isOnPaidPlan = ["starter", "growth", "pro", "enterprise"].includes(billing.plan);
  const showPlanCards = true;
  const plansById: Record<string, any> = Object.fromEntries(
    (plansData?.plans || []).map((p: any) => [p.id, p])
  );

  return (
    <div>
      <LoadingOverlay active={checkoutMut.isPending || portalMut.isPending} />
      {/* ── Plan status card ── */}
      <div style={{
        background: "white", border: "1px solid #e2e8f0", borderRadius: 14,
        padding: "20px 24px", marginBottom: 16,
      }}>
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", gap: isMobile ? 8 : 0, marginBottom: isOnPaidPlan ? 12 : 0 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1e293b" }}>
              {PLAN_LABEL[billing.plan] || billing.plan}
              {billing.is_grandfathered && <span style={{ marginLeft: 8, fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>legacy</span>}
            </div>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
              {billing.is_grandfathered
                ? "Grandfathered account — no usage limits apply"
                : billing.trial_days_left != null && (billing.effective_status === "active" || billing.effective_status === "trialing")
                ? (billing.effective_status === "trialing"
                    ? `Trialing — card charges in ${billing.trial_days_left} day${billing.trial_days_left !== 1 ? "s" : ""}`
                    : `Trial — ${billing.trial_days_left} day${billing.trial_days_left !== 1 ? "s" : ""} left`)
                : billing.effective_status === "grace"
                ? `Grace period — ${billing.grace_days_left} day${billing.grace_days_left !== 1 ? "s" : ""} left`
                : `Renews ${billing.reset_date}`}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              background: chip.bg, color: chip.color, border: `1px solid ${chip.border}`,
              padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            }}>{billing.effective_status}</span>
          </div>
        </div>
        {isOnPaidPlan && (
          <button
            onClick={() => portalMut.mutate()}
            disabled={portalMut.isPending}
            style={{
              padding: "8px 18px", borderRadius: 8, fontSize: 13, border: "1px solid #cbd5e1",
              background: "white", color: NAVY, fontWeight: 700, cursor: "pointer",
            }}
          >
            {portalMut.isPending ? "Opening…" : "Manage Billing →"}
          </button>
        )}
      </div>

      {/* ── Plan cards ── */}
      {showPlanCards && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", gap: isMobile ? 10 : 0, marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>
              {isOnPaidPlan ? "Available Plans" : "Choose a Plan"}
            </div>
            {/* Monthly / Annual toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{
                display: "flex", background: "#f1f5f9", borderRadius: 8, padding: 3, gap: 2,
              }}>
                {(["monthly", "annual"] as const).map((p) => (
                  <button key={p} onClick={() => setBillingPeriod(p)} style={{
                    padding: "5px 14px", borderRadius: 6, border: "none", fontSize: 12,
                    fontWeight: 700, cursor: "pointer", transition: "all 0.15s",
                    background: billingPeriod === p ? "white" : "transparent",
                    color: billingPeriod === p ? NAVY : "#94a3b8",
                    boxShadow: billingPeriod === p ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
                  }}>
                    {p === "monthly" ? "Monthly" : "Annual"}
                  </button>
                ))}
              </div>
              {billingPeriod === "annual" && (
                <span style={{
                  background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 700,
                  padding: "3px 8px", borderRadius: 20,
                }}>Save 17%</span>
              )}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
            {(["starter", "growth", "pro"] as const).map((plan) => {
              const f = plansById[plan];
              if (!f) return null;
              const isCurrent = billing.plan === plan;
              const isPopular = plan === "growth";
              const busy = checkoutMut.isPending && checkoutMut.variables?.plan === plan;
              const annualMonthly = Math.round(f.annual_price_usd / 12);
              const annualTotal = f.annual_price_usd;
              const displayPrice = billingPeriod === "annual" ? annualMonthly : f.monthly_price_usd;
              return (
                <div key={plan} style={{
                  background: "white",
                  border: isCurrent ? `2px solid ${NAVY}` : isPopular ? `2px solid ${TEAL}` : "1px solid #e2e8f0",
                  borderRadius: 14, padding: "20px 18px", position: "relative",
                  boxShadow: isPopular && !isCurrent ? "0 4px 16px rgba(0,201,167,0.12)" : undefined,
                }}>
                  {isPopular && !isCurrent && (
                    <div style={{
                      position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)",
                      background: TEAL, color: "white", fontSize: 10, fontWeight: 800,
                      padding: "3px 10px", borderRadius: 20, letterSpacing: 0.5,
                    }}>POPULAR</div>
                  )}
                  {isCurrent && (
                    <div style={{
                      position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)",
                      background: NAVY, color: "white", fontSize: 10, fontWeight: 800,
                      padding: "3px 10px", borderRadius: 20, letterSpacing: 0.5,
                    }}>CURRENT</div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>{PLAN_LABEL[plan]}</div>
                  <div style={{ marginBottom: billingPeriod === "annual" ? 4 : 12 }}>
                    <span style={{ fontSize: 24, fontWeight: 800, color: NAVY }}>${displayPrice}</span>
                    <span style={{ fontSize: 13, fontWeight: 400, color: "#64748b" }}>/mo</span>
                    {billingPeriod === "annual" && (
                      <span style={{ marginLeft: 6, fontSize: 12, color: "#64748b", textDecoration: "line-through" }}>${f.monthly_price_usd}</span>
                    )}
                  </div>
                  {billingPeriod === "annual" && (
                    <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 600, marginBottom: 12 }}>
                      ${annualTotal} billed annually
                    </div>
                  )}
                  {/* A tier buys Core alone now, so CVs/month and AI operations/month
                      no longer belong here — they are Recruitment and Finance
                      allowances, and listing them as tier features is what made the
                      old card read as "Growth includes an ATS"
                      (module-aware-quota-ui AC8).
                      ai_doc is split the same way (Core 50 + Recruitment 100 on
                      Growth), so it reads from core_limits too — f.limits is the
                      full-catalogue table and overstated it by 3x.
                      Omitted rather than defaulted when core_limits is missing: a
                      frontend that outran its backend during a rolling deploy would
                      otherwise print "0 AI documents/month" on a card asking for money. */}
                  <div style={{ fontSize: 12, color: "#475569", display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
                    <div>👥 Up to <strong>{f.included_employees}</strong> employees</div>
                    {f.core_limits?.ai_doc != null && (
                      <div>📄 <strong>{f.core_limits.ai_doc.toLocaleString()}</strong> AI documents/month</div>
                    )}
                    <div style={{ color: "#94a3b8" }}>🧩 Module quotas scale with this tier</div>
                  </div>
                  <button
                    onClick={() => !isCurrent && checkoutMut.mutate({ plan, billing_period: billingPeriod })}
                    disabled={isCurrent || checkoutMut.isPending}
                    style={{
                      width: "100%", padding: "9px 0", borderRadius: 8, fontSize: 13,
                      border: "none", fontWeight: 700, cursor: isCurrent ? "default" : "pointer",
                      background: isCurrent ? "#f1f5f9" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
                      color: isCurrent ? "#94a3b8" : "white",
                      opacity: checkoutMut.isPending && !busy ? 0.6 : 1,
                    }}
                  >
                    {busy
                      ? (isOnPaidPlan ? "Switching…" : "Redirecting…")
                      : isCurrent ? "Current Plan" : (isOnPaidPlan ? "Switch Plan →" : "Subscribe →")}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* ── Plan quota meters ── */}
      {!billing.is_grandfathered && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 24px", marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 16 }}>
            Monthly quota
            {/* "Defined by Growth plan" became a half-truth when a tier started buying
                Core alone — the CV allowance arrives with Recruitment, not the tier
                (module-aware-quota-ui AC7). */}
            <span style={{ marginLeft: 8, fontSize: 12, color: "#94a3b8", fontWeight: 400 }}>
              Defined by your {PLAN_LABEL[billing.plan] || billing.plan} plan and modules · resets {billing.meter_reset_date}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {billing.meters.map((m: any) => {
              const pct = m.pct ?? 0;
              const barColor = pct >= 100 ? "#dc2626" : pct >= 80 ? "#f59e0b" : NAVY;
              const unlimited = m.limit >= 9000;
              return (
                <div key={m.meter}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                      {m.label}
                      {/* Which module this allowance came from, so the tier price and
                          the module price each have something visible to explain (AC7). */}
                      {m.module_label && (
                        <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>
                          · {m.module_label}
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 13, color: pct >= 100 ? "#dc2626" : pct >= 80 ? "#f59e0b" : "#64748b" }}>
                      {unlimited ? <span style={{ color: "#16a34a" }}>Unlimited</span> : `${m.used} / ${m.limit}`}
                      {!unlimited && <span style={{ color: "#94a3b8", fontSize: 11 }}> ({pct}%)</span>}
                    </span>
                  </div>
                  {!unlimited && (
                    <div style={{ background: "#f1f5f9", borderRadius: 6, height: 7, overflow: "hidden" }}>
                      <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: barColor, borderRadius: 6, transition: "width 0.4s" }} />
                    </div>
                  )}
                  {/* Module removed mid-month but the counter already ran. The row stays
                      until the meter resets rather than disappearing with usage on it —
                      an overage may already have accrued and it will show up on the
                      invoice whether or not this panel admits it (AC6). */}
                  {m.module_revoked && (
                    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                      {m.module_label} was removed this month — this usage still counts
                      toward your next invoice. Resets {billing.meter_reset_date}.
                    </div>
                  )}
                  {/* Overage notice (billing-module-enhancement Phase 2b) — meters with an
                      overage_ceiling soft-bill past `limit` instead of blocking immediately;
                      surface that a charge is now accruing instead of letting it be a
                      surprise on next month's invoice. */}
                  {!unlimited && m.overage_ceiling && m.used > m.limit && (
                    <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 4 }}>
                      {m.used - m.limit} over your included amount — billing at ${m.overage_rate_usd}/extra,
                      up to {m.overage_ceiling} total this month.
                    </div>
                  )}
                  {/* Employee overage (ai-usage-tenant-visibility AC6) — peak-based, no
                      ceiling concept like the AI meters (the plan's hard employee cap
                      above already IS the ceiling), so this gets its own notice. Shows
                      the accrued-so-far charge for THIS month, which can only rise until
                      it's finalized on the 1st of next month (AC6b). */}
                  {m.meter === "employees" && m.overage_units > 0 && (
                    <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 4 }}>
                      Peak {m.overage_peak} active this month — {m.overage_units} over your
                      included {m.overage_included} — accruing ${m.overage_usd_accrued} so far
                      at ${m.overage_rate_usd}/extra employee, finalized on your next invoice.
                    </div>
                  )}
                  {/* Proactive forecast (billing-module-enhancement AC14) — only shown once
                      trending toward the limit, and only once enough usage history exists
                      to project (backend withholds it early in the month to avoid noise). */}
                  {!unlimited && pct >= 80 && !m.overage_ceiling && m.forecast_days_to_limit != null && (
                    <div style={{ fontSize: 11, color: pct >= 100 ? "#dc2626" : "#f59e0b", marginTop: 4 }}>
                      {m.forecast_days_to_limit === 0
                        ? "Limit reached — upgrade to keep going"
                        : `At this pace, you'll hit the limit in ~${m.forecast_days_to_limit} day${m.forecast_days_to_limit === 1 ? "" : "s"}`}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── AI feature breakdown, grouped by billed meter ──
          Each group's total must equal that meter's `used` value in ai.meters above —
          both come from the same FEATURE_METER_MAP-driven backend query, so they
          reconcile by construction (ai-usage-tenant-visibility AC4). */}
      {ai?.breakdown?.length > 0 && (() => {
        const meterLabel: Record<string, string> = {};
        (ai.meters || []).forEach((m: any) => { meterLabel[m.meter] = m.label; });
        const groups: Record<string, any[]> = {};
        ai.breakdown.forEach((row: any) => {
          if (!groups[row.meter]) groups[row.meter] = [];
          groups[row.meter].push(row);
        });
        return (
          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 24px", marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 16px", color: NAVY, fontSize: 14, fontWeight: 700 }}>
              AI usage this month — by feature
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {Object.entries(groups).map(([meter, rows]) => {
                const groupTotal = rows.reduce((sum, r) => sum + r.calls, 0);
                return (
                  <div key={meter}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.3 }}>
                        {meterLabel[meter] || meter}
                      </span>
                      <span style={{ fontSize: 12, color: "#94a3b8" }}>{groupTotal} calls</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {rows.map((row: any, i: number) => {
                        const color = FEATURE_COLORS[row.feature] || "#64748b";
                        return (
                          <div key={row.feature + i} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "10px 14px", background: "#f8fafc", borderRadius: 8, borderLeft: `3px solid ${color}`,
                          }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <span style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{FEATURE_LABELS[row.feature] || row.feature}</span>
                            </div>
                            <div style={{ display: "flex", gap: 20, fontSize: 13 }}>
                              <span style={{ color: "#64748b" }}><strong style={{ color: "#1e293b" }}>{row.calls}</strong> calls</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Monthly trend ──
          Labeled explicitly (title was previously just "Monthly trend" with bare numbers
          and a zero-padded month digit like "07", giving users no way to tell what the
          chart represented) and month axis now shows a real month name. */}
      {ai?.trend?.length > 0 && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 24px" }}>
          <h3 style={{ margin: "0 0 4px", color: NAVY, fontSize: 14, fontWeight: 700 }}>AI usage — monthly trend</h3>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 16 }}>
            Billed AI calls per month, across all metered features, last 6 months
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
            {ai.trend.slice(-6).map((m: any) => {
              const maxCalls = Math.max(...ai.trend.map((x: any) => x.calls), 1);
              const h = Math.max(8, Math.round((m.calls / maxCalls) * 80));
              const [y, mo] = m.month.split("-").map(Number);
              const monthLabel = new Date(y, mo - 1, 1).toLocaleDateString(undefined, { month: "short" });
              return (
                <div key={m.month} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{m.calls}</div>
                  <div style={{ height: h, background: NAVY, borderRadius: "4px 4px 0 0", opacity: 0.85 }} />
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>{monthLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Invoice / payment history (billing-module-enhancement AC13) ── */}
      {!billing.is_grandfathered && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: "20px 24px", marginBottom: 16 }}>
          <h3 style={{ margin: "0 0 16px", color: NAVY, fontSize: 14, fontWeight: 700 }}>Invoice history</h3>
          {invoiceData?.invoices?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {invoiceData.invoices.map((inv: any, i: number) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ color: "#64748b" }}>{inv.date}</span>
                    <span style={{ color: "#1e293b" }}>{inv.description}</span>
                    {inv.source === "manual" && (
                      <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 20, fontWeight: 600, background: "#f1f5f9", color: "#64748b" }}>
                        manual
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontWeight: 700, color: "#1e293b" }}>${inv.amount_usd.toFixed(2)}</span>
                    {inv.invoice_pdf && (
                      <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" style={{ color: NAVY, fontSize: 12, textDecoration: "none", fontWeight: 600 }}>
                        PDF ↗
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8", fontSize: 13 }}>
              No invoices or payments yet
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {(!ai?.breakdown?.length && !billing.is_grandfathered) && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📊</div>
          <div style={{ fontWeight: 600, color: "#64748b" }}>No AI usage yet this month</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Usage will appear here as your team uses AI features</div>
        </div>
      )}
    </div>
  );
}

// ── AI Provider tab ─────────────────────────────────────────────────────────

function AiTab() {
  const qc = useQueryClient();
  const [pullMsg, setPullMsg] = useState("");
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const settingsMutation = useMutation({ mutationFn: updateSettings, onSuccess: () => qc.invalidateQueries({ queryKey: ["settings"] }) });
  const pullMutation = useMutation({
    mutationFn: (model: string) => pullModel(model),
    onMutate: (m) => setPullMsg(`Pulling ${m}... this may take a few minutes`),
    onSuccess: (_, m) => { setPullMsg(`${m} ready`); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: () => setPullMsg("Pull failed. Check Ollama is running."),
  });

  const ollamaRunning = settings?.ollama_status?.running;
  const pulledModels: string[] = settings?.ollama_status?.models || [];
  const currentProvider = settings?.ai_provider || "ollama";
  const currentModel = settings?.ollama_model || "qwen2.5:7b";

  return (
    <div style={card}>
      <LoadingOverlay active={settingsMutation.isPending || pullMutation.isPending} />
      <h3 style={sectionHead}>AI Provider</h3>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
        Choose where AI runs. Ollama is free and local. Google requires billing.
      </p>
      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        {[
          { id: "ollama", icon: "🦙", title: "Ollama (Local)", badge: "Free · No quota", sub: "Runs on your server", status: ollamaRunning ? "● Running" : "○ Not Running", statusColor: ollamaRunning ? "#16a34a" : "#dc2626", statusBg: ollamaRunning ? "#dcfce7" : "#fee2e2" },
          { id: "google", icon: "🌐", title: "Google Gemini", badge: "Paid · Requires billing", sub: "Cloud API · Very fast", status: "~$0.01/JD", statusColor: "#854d0e", statusBg: "#fef9c3" },
        ].map(p => (
          <div key={p.id} onClick={() => settingsMutation.mutate({ ai_provider: p.id })} style={{ ...providerCard, border: `2px solid ${currentProvider === p.id ? TEAL : "#e2e8f0"}`, background: currentProvider === p.id ? "#f0fdf9" : "white", cursor: "pointer" }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{p.icon}</div>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>{p.title}</div>
            <div style={{ fontSize: 12, color: "#d97706", fontWeight: 600, marginTop: 2 }}>{p.badge}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{p.sub}</div>
            <div style={{ marginTop: 8 }}><span style={{ background: p.statusBg, color: p.statusColor, padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{p.status}</span></div>
            {currentProvider === p.id && <div style={{ marginTop: 6, fontSize: 11, color: TEAL, fontWeight: 700 }}>✓ ACTIVE</div>}
          </div>
        ))}
      </div>

      {currentProvider === "ollama" && (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: NAVY, marginBottom: 10 }}>Select Model</div>
          <div style={{ display: "grid", gap: 8 }}>
            {settings?.available_models?.map((m: any) => {
              const isPulled = pulledModels.some((p: string) => p.startsWith(m.id.split(":")[0]));
              const isSelected = currentModel === m.id;
              return (
                <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 8, border: `1.5px solid ${isSelected ? TEAL : "#e2e8f0"}`, background: isSelected ? "#f0fdf9" : "#f8fafc" }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13, color: NAVY }}>{m.name}</span>
                    <span style={{ fontSize: 11, color: "#64748b", marginLeft: 8 }}>{m.size}</span>
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: m.quality === "Best" ? "#16a34a" : m.quality === "Good" ? "#2563eb" : "#d97706" }}>{m.quality}</span>
                  </div>
                  <div>
                    {isPulled ? (
                      <button onClick={() => settingsMutation.mutate({ ollama_model: m.id })} style={{ background: isSelected ? TEAL : "#e2e8f0", color: isSelected ? "white" : "#334155", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>{isSelected ? "✓ Selected" : "Use"}</button>
                    ) : (
                      <button onClick={() => pullMutation.mutate(m.id)} disabled={pullMutation.isPending} style={{ background: NAVY, color: "white", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12, opacity: pullMutation.isPending ? 0.6 : 1 }}>⬇ Download</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {pullMsg && <div style={{ marginTop: 10, fontSize: 12, color: "#d97706" }}>{pullMsg}</div>}
          {!ollamaRunning && <div style={{ marginTop: 12, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#92400e" }}>⚠ Ollama is starting up. Wait ~30 seconds and refresh.</div>}
        </div>
      )}
    </div>
  );
}

// ── AI Provider tab ─────────────────────────────────────────────────────────



// ── LinkedIn tab ─────────────────────────────────────────────────────────────

function SocialPlatformCard({
  title, icon, connected, connectedLabel, notConnectedLabel,
  onDisconnect, disconnecting, connectUrl, connectLabel, platformReady, platformNote,
  extra,
}: {
  title: string; icon: React.ReactNode;
  connected: boolean; connectedLabel: string; notConnectedLabel: string;
  onDisconnect: () => void; disconnecting: boolean;
  connectUrl?: string; connectLabel: string;
  platformReady?: boolean; platformNote?: string;
  extra?: React.ReactNode;
}) {
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        {icon}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 15 }}>{title}</div>
          <div style={{ fontSize: 12, color: connected ? "#16a34a" : "#64748b", marginTop: 2 }}>
            {connected ? connectedLabel : notConnectedLabel}
          </div>
        </div>
        {connected && (
          <button
            onClick={onDisconnect}
            disabled={disconnecting}
            style={{ background: "#fee2e2", color: "#dc2626", border: "none", padding: "7px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          >{disconnecting ? "Disconnecting…" : "Disconnect"}</button>
        )}
      </div>
      {extra}
      {platformReady === false && !connected ? (
        <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "12px 16px", fontSize: 13, color: "#92400e" }}>
          {platformNote || `${title} isn't set up for this platform yet. Contact HRxAI support to enable it.`}
        </div>
      ) : connectUrl ? (
        // Reconnect stays reachable while connected. An expired token still reports as
        // "connected" — the status check only proves a token string exists, not that it
        // still works — so this link is the only way back for a lapsed authorisation.
        connected ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <a href={connectUrl}
              style={{ display: "inline-flex", alignItems: "center", background: "white", color: NAVY, border: `1px solid ${NAVY}`, padding: "8px 16px", borderRadius: 7, textDecoration: "none", fontWeight: 600, fontSize: 13 }}>
              {connectLabel}
            </a>
            <span style={{ fontSize: 12, color: "#64748b" }}>
              Posts failing? {title} authorisation expires periodically — reconnect to refresh it.
            </span>
          </div>
        ) : (
          <a href={connectUrl}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, background: NAVY, color: "white", padding: "10px 20px", borderRadius: 7, textDecoration: "none", fontWeight: 600, fontSize: 14 }}>
            {connectLabel}
          </a>
        )
      ) : null}
    </div>
  );
}

function SocialTab() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: liAuthData } = useQuery({ queryKey: ["li-auth"], queryFn: getLinkedInAuthUrl, retry: false });
  const { data: liStatus, isLoading: liLoading } = useQuery({ queryKey: ["li-user-status"], queryFn: getLinkedInUserStatus, retry: false });

  const liDisconnect = useMutation({
    mutationFn: disconnectLinkedIn,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["li-user-status"] }); toast("LinkedIn disconnected", "info"); },
  });

  // Handle LinkedIn OAuth redirect
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const li = params.get("li");
    if (li === "connected") {
      toast(`LinkedIn connected as ${params.get("name") || "your profile"}!`, "success");
      qc.invalidateQueries({ queryKey: ["li-user-status"] });
    } else if (li === "error") {
      toast(`LinkedIn failed: ${params.get("reason")}`, "error");
    }
    if (li) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const liConnected = (liStatus as any)?.connected;
  const liName      = (liStatus as any)?.person_name;

  return (
    <div>
      <LoadingOverlay active={liDisconnect.isPending} />
      <div style={{ ...card, background: "#f0f9ff", border: "1px solid #bae6fd", marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#0369a1" }}>
          <strong>Job card images</strong> — every LinkedIn post includes a branded 1080×1080 image with your company logo,
          job title, and location. Set up your logo and branding in the <strong>Branding</strong> tab.
        </div>
      </div>

      {/* LinkedIn */}
      <SocialPlatformCard
        title="LinkedIn"
        icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="#0a66c2"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>}
        connected={liConnected}
        connectedLabel={`Posting as: ${liName || "your LinkedIn profile"}`}
        notConnectedLabel="Connect your personal LinkedIn to post jobs with a branded image card"
        onDisconnect={() => liDisconnect.mutate()}
        disconnecting={liDisconnect.isPending}
        connectUrl={!liLoading ? (liAuthData as any)?.auth_url : undefined}
        connectLabel={liConnected ? "Reconnect LinkedIn" : "Connect LinkedIn Account"}
        platformReady={(liAuthData as any)?.auth_url !== undefined ? true : false}
        platformNote="LinkedIn posting isn't set up for this platform yet. This is a platform-level setting, not a workspace one — contact HRxAI support to enable it."
      />

      <div style={{ ...card, background: "#f8fafc", border: "none" }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          To post jobs to LinkedIn, go to any job and use the <strong>Social Sharing</strong> section.
        </div>
      </div>
    </div>
  );
}

// ── Email & SMTP tab ─────────────────────────────────────────────────────────

function EmailTab() {
  const qc = useQueryClient();
  const { data: integrations } = useQuery({ queryKey: ["integrations"], queryFn: getIntegrations });
  const { data: gmailStatus, refetch: refetchGmail } = useQuery({ queryKey: ["gmail-status"], queryFn: getGmailStatus });
  const smtp = (integrations as any)?.smtp || {};

  // Handle redirect back from Gmail OAuth
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail_connected") === "1") {
      refetchGmail();
      window.history.replaceState({}, "", window.location.pathname + "?tab=email");
    }
  }, []);

  const [form, setForm] = useState<Record<string, string>>({});
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
  const val = (k: string) => form[k] ?? smtp[k] ?? "";

  const [testMsg, setTestMsg] = useState("");
  const saveMutation = useMutation({
    mutationFn: (data: object) => saveSmtp(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["integrations"] }); },
  });
  const testMutation = useMutation({
    mutationFn: testSmtp,
    onSuccess: (d: any) => setTestMsg(d.success ? "Test email sent successfully!" : `Failed: ${d.error}`),
  });

  const handleSave = () => {
    const payload: Record<string, string> = {};
    const map: Record<string, string> = {
      host: "smtp.host", port: "smtp.port", user: "smtp.user",
      password: "smtp.password", sender_email: "smtp.sender_email",
      notify_hr_email: "smtp.notify_hr_email", jobs_email: "intake.jobs_email",
    };
    Object.entries(map).forEach(([field, key]) => {
      if (form[field] !== undefined) payload[key] = form[field];
    });
    saveMutation.mutate(payload);
  };

  return (
    <div>
      <LoadingOverlay active={saveMutation.isPending || testMutation.isPending} />
      {/* Status banner */}
      <div style={{ ...card, borderLeft: `4px solid ${smtp.configured ? TEAL : NAVY}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 24 }}>{smtp.configured ? "✅" : "📧"}</span>
          <div>
            <div style={{ fontWeight: 700, color: NAVY }}>
              {smtp.configured ? "Using your own SMTP" : "Using HRxAI platform email"}
            </div>
            <div style={{ fontSize: 13, color: "#64748b" }}>
              {smtp.configured
                ? `Emails sent from ${smtp.sender_email} · HR alerts to ${smtp.notify_hr_email}`
                : "All notifications are sent from hrxai@sequencetechno.com with your company name. Configure your own SMTP below to send from your own domain."}
            </div>
          </div>
        </div>
      </div>

      {/* SMTP override */}
      <div style={card}>
        <StepHeader n={1} title="Your own SMTP (optional — overrides platform email)" done={smtp.configured} />

        {/* Gmail App Password instructions */}
        <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: "#1e3a8a", marginBottom: 8 }}>Using Gmail? You need an App Password</div>
          <p style={{ fontSize: 12, color: "#334155", margin: "0 0 10px" }}>
            Google does not allow your normal Gmail password here. You must generate a special <strong>App Password</strong>. This takes 2 minutes:
          </p>
          <ol style={{ fontSize: 12, color: "#334155", margin: 0, paddingLeft: 18, lineHeight: 2 }}>
            <li>Make sure <strong>2-Step Verification</strong> is enabled on your Google account —
              {" "}<a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" style={{ color: "#1d4ed8" }}>myaccount.google.com/security</a>
            </li>
            <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{ color: "#1d4ed8" }}>myaccount.google.com/apppasswords</a></li>
            <li>Click <strong>Create</strong> → name it <code style={code}>Sequence HR</code> → click <strong>Create</strong></li>
            <li>Copy the 16-character password shown (no spaces) and paste it into the <strong>Password</strong> field below</li>
          </ol>
          <p style={{ fontSize: 12, color: "#64748b", margin: "10px 0 0" }}>
            For <strong>Google Workspace / Office 365 / Zoho</strong>: use your normal email credentials or an SMTP relay — check your provider's docs.
          </p>
        </div>

        <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="SMTP Host (Gmail: smtp.gmail.com)" placeholder="smtp.gmail.com" value={val("host")} onChange={set("host")} />
          <Field label="SMTP Port" placeholder="587" value={val("port")} onChange={set("port")} type="number" />
          <Field label="Username / Email" placeholder="hr@yourcompany.com" value={val("user")} onChange={set("user")} />
          <Field label="Password / App Password" placeholder="••••••••" value={val("password")} onChange={set("password")} type="password" />
          <Field label="Sender Display Email" placeholder="hr@yourcompany.com" value={val("sender_email")} onChange={set("sender_email")} />
          <Field label="HR Notification Email (receives CV alerts)" placeholder="hr-team@yourcompany.com" value={val("notify_hr_email")} onChange={set("notify_hr_email")} />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <button onClick={handleSave} disabled={saveMutation.isPending} style={saveBtn}>{saveMutation.isPending ? "Saving..." : "Save SMTP Config"}</button>
          <button onClick={() => testMutation.mutate()} disabled={testMutation.isPending || !smtp.configured} style={{ ...saveBtn, background: "#f1f5f9", color: "#334155" }}>{testMutation.isPending ? "Sending..." : "Send Test Email"}</button>
        </div>
        {testMsg && <div style={{ marginTop: 10, fontSize: 13, color: testMsg.startsWith("Test") ? "#16a34a" : "#dc2626" }}>{testMsg}</div>}
        {saveMutation.isSuccess && <div style={{ marginTop: 8, fontSize: 13, color: "#16a34a" }}>Saved.</div>}
      </div>

      {/* CV intake email */}
      <div style={card}>
        <StepHeader n={2} title="CV intake email address" done={!!smtp.jobs_email} />
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
          Candidates send CVs to this address. You can customise it to match your client's preference
          (e.g. <code style={code}>jobs+clientname@hrxai.app</code>).
          The system auto-generated one for your workspace — edit it below if needed.
        </p>
        {smtp.jobs_email && !form.jobs_email && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0fdf9", border: "1px solid #99f6e4", borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
            <code style={{ flex: 1, fontSize: 13, color: NAVY, fontWeight: 600 }}>{smtp.jobs_email}</code>
            <button
              onClick={() => { navigator.clipboard.writeText(smtp.jobs_email); }}
              style={{ background: TEAL, color: "white", border: "none", padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
            >Copy</button>
          </div>
        )}
        <Field label="Edit intake email" placeholder="jobs+yourcompany@hrxai.app" value={val("jobs_email")} onChange={set("jobs_email")} />
        <button onClick={() => saveMutation.mutate({ "intake.jobs_email": form.jobs_email || smtp.jobs_email })} disabled={saveMutation.isPending} style={{ ...saveBtn, marginTop: 12 }}>
          {saveMutation.isPending ? "Saving..." : "Save Intake Email"}
        </button>
        {saveMutation.isSuccess && <div style={{ marginTop: 8, fontSize: 13, color: "#16a34a" }}>Saved.</div>}
      </div>

      {/* Gmail OAuth connect */}
      <GmailConnectSection gmailStatus={gmailStatus} refetchGmail={refetchGmail} />
    </div>
  );
}

function GmailConnectSection({ gmailStatus, refetchGmail }: { gmailStatus: any; refetchGmail: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [disconnecting, setDisconnecting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [credsStatus, setCredsStatus] = useState<any>(null);

  React.useEffect(() => {
    getGmailCredentialsStatus().then(setCredsStatus).catch(() => {});
  }, [uploadMsg]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const data = await uploadGmailCredentials(fd);
      setUploadMsg(`✓ Credentials saved (${data.client_id_prefix})`);
    } catch (err: any) {
      setUploadMsg(`Error: ${err?.response?.data?.detail || "Upload failed"}`);
    }
    setUploading(false);
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    await disconnectGmail();
    refetchGmail();
    qc.invalidateQueries({ queryKey: ["integrations"] });
    setDisconnecting(false);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const data = await getGmailAuthUrl();
      if (data.auth_url) window.location.href = data.auth_url;
      else { toast(data.detail || "Could not get auth URL", "error"); setConnecting(false); }
    } catch (err: any) {
      toast(err?.response?.data?.detail || "Could not get auth URL", "error");
      setConnecting(false);
    }
  };

  const connected = gmailStatus?.connected;
  const hasCreds = credsStatus?.has_own_credentials;

  return (
    <div style={card}>
      <LoadingOverlay active={connecting || disconnecting || uploading} />
      <StepHeader n={3} title="Connect Gmail inbox for CV intake" done={connected} />
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
        Connect your own Gmail account so the system watches your inbox for incoming CVs — fully isolated from other tenants.
      </p>

      {connected ? (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "12px 16px", marginBottom: 10 }}>
            <span style={{ fontSize: 20 }}>✅</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: "#15803d" }}>Gmail Connected</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{gmailStatus.email}</div>
            </div>
            <button onClick={handleDisconnect} disabled={disconnecting}
              style={{ background: "#fee2e2", color: "#dc2626", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              {disconnecting ? "Disconnecting..." : "Disconnect"}
            </button>
          </div>
          <p style={{ fontSize: 12, color: "#64748b" }}>The email watcher polls this inbox every 10 minutes for CV attachments.</p>
        </div>
      ) : (
        <div>
          {/* Step A: Create Google Cloud project + credentials */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 10 }}>Step A — Create your Google Cloud OAuth credentials</div>
            <ol style={{ fontSize: 12, color: "#334155", margin: 0, paddingLeft: 18, lineHeight: 2.2 }}>
              <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" style={{ color: "#1d4ed8" }}>console.cloud.google.com</a> → create or select a project</li>
              <li><strong>APIs & Services → Library</strong> → search <strong>Gmail API</strong> → <strong>Enable</strong></li>
              <li><strong>APIs & Services → OAuth consent screen</strong> → choose <strong>External</strong> → fill app name + support email → Save</li>
              <li><strong>APIs & Services → Credentials → Create Credentials → OAuth client ID</strong></li>
              <li>Application type: <strong>Web application</strong></li>
              <li>Under <strong>Authorised redirect URIs</strong> add:
                <code style={{ ...code, display: "inline-block", margin: "4px 0 0 6px", padding: "2px 8px" }}>
                  {window.location.origin.replace("30300", "30800")}/api/gmail/auth/callback
                </code>
              </li>
              <li>Click <strong>Create</strong> → then click <strong>Download JSON</strong></li>
            </ol>
          </div>

          {/* Step B: Upload credentials JSON */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 10 }}>Step B — Upload your credentials JSON</div>
            {hasCreds && (
              <div style={{ fontSize: 12, color: "#16a34a", fontWeight: 600, marginBottom: 8 }}>
                ✓ Credentials uploaded ({credsStatus.client_id_prefix})
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, background: NAVY, color: "white", padding: "8px 16px", borderRadius: 7, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                {uploading ? "Uploading..." : hasCreds ? "Re-upload credentials JSON" : "Upload credentials JSON"}
                <input type="file" accept=".json" onChange={handleUpload} style={{ display: "none" }} disabled={uploading} />
              </label>
            </div>
            {uploadMsg && (
              <div style={{ marginTop: 8, fontSize: 12, color: uploadMsg.startsWith("✓") ? "#16a34a" : "#dc2626" }}>{uploadMsg}</div>
            )}
          </div>

          {/* Step C: Connect */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: NAVY, marginBottom: 10 }}>Step C — Connect your Gmail account</div>
            <button onClick={handleConnect} disabled={!hasCreds || connecting}
              style={{ display: "inline-flex", alignItems: "center", gap: 10, background: hasCreds ? "white" : "#f1f5f9", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "10px 18px", color: hasCreds ? "#1e293b" : "#94a3b8", fontWeight: 600, fontSize: 14, cursor: hasCreds && !connecting ? "pointer" : "not-allowed", boxShadow: hasCreds ? "0 1px 3px rgba(0,0,0,0.08)" : "none", opacity: connecting ? 0.6 : 1 }}>
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill={hasCreds ? "#EA4335" : "#cbd5e1"} d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill={hasCreds ? "#4285F4" : "#cbd5e1"} d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill={hasCreds ? "#FBBC05" : "#cbd5e1"} d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill={hasCreds ? "#34A853" : "#cbd5e1"} d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.29-8.16 2.29-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {connecting ? "Connecting…" : "Connect Google / Gmail Account"}
            </button>
            {!hasCreds && <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>Upload your credentials JSON first (Step B).</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Clients tab ──────────────────────────────────────────────────────────────

const CLIENT_TAX_SCHEMES: Record<string, string> = {
  "":           "None / Custom",
  custom:       "Custom",
  thailand:     "🇹🇭 Thailand",
  india:        "🇮🇳 India",
  singapore:    "🇸🇬 Singapore",
  uk:           "🇬🇧 United Kingdom",
  uae:          "🇦🇪 UAE",
  australia:    "🇦🇺 Australia",
  zero_rated:   "Zero-rated / Export",
};

function ClientsTab() {
  const confirm = useConfirm();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<ClientFormValues>(BLANK_CLIENT_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState("");
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: getClients });
  const formRef = useRef<HTMLDivElement>(null);

  // The Add/Edit form renders below the full client list — with enough clients,
  // it opens off-screen with no indication anything happened (bug-49/57).
  useEffect(() => {
    if (showForm) formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [showForm]);

  const createMut = useMutation({
    mutationFn: (values: ClientFormValues) => createClient(clientValuesToFormData(values)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      setForm(BLANK_CLIENT_FORM); setShowForm(false);
      setMsg("Client added.");
    },
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to save client", "error"),
  });

  const updateMut = useMutation({
    mutationFn: (values: ClientFormValues) => updateClient(editingId!, clientValuesToFormData(values)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      setForm(BLANK_CLIENT_FORM); setEditingId(null); setShowForm(false);
      setMsg("Client updated.");
    },
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to save client", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteClient(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); setMsg("Client deleted."); },
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to delete client", "error"),
  });

  const startEdit = (c: any) => {
    setForm({ name: c.name || "", contact_email: c.contact_email || "", contact_person: c.contact_person || "", address: c.address || "", tax_id: c.tax_id || "", tax_label: c.tax_label || "", tax_scheme: c.tax_scheme || "", branch_number: c.branch_number || "" });
    setEditingId(c.id);
    setShowForm(true);
  };

  const cancel = () => { setForm(BLANK_CLIENT_FORM); setEditingId(null); setShowForm(false); };

  const handleSubmit = (values: ClientFormValues) => {
    setMsg("");
    if (editingId) updateMut.mutate(values); else createMut.mutate(values);
  };

  return (
    <div style={card}>
      <LoadingOverlay active={createMut.isPending || updateMut.isPending || deleteMut.isPending} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h3 style={{ ...sectionHead, marginBottom: 0 }}>Clients ({(clients as any[]).length})</h3>
        {!showForm && (
          <button onClick={() => setShowForm(true)} style={saveBtn}>+ Add Client</button>
        )}
      </div>

      {/* Client list */}
      <div style={{ display: "grid", gap: 8, marginBottom: showForm ? 16 : 0 }}>
        {(clients as any[]).map((c: any) => (
          <div key={c.id} style={{ background: "#f8fafc", borderRadius: 7, padding: "12px 14px", border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontWeight: 600, color: "#1e293b", fontSize: 14 }}>{c.name}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 14px", fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  {c.contact_person && <span>👤 {c.contact_person}</span>}
                  {c.contact_email && <span>✉ {c.contact_email}</span>}
                  {c.tax_id && <span style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 4, padding: "1px 5px" }}>{c.tax_label || "Tax ID"}: {c.tax_id}</span>}
                  {c.branch_number && <span style={{ background: "#fefce8", color: "#854d0e", border: "1px solid #fde047", borderRadius: 4, padding: "1px 5px" }}>Branch: {c.branch_number}</span>}
                </div>
                {c.address && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{c.address}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0, marginLeft: 10 }}>
                <button onClick={() => startEdit(c)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontSize: 12, color: "#475569" }}>Edit</button>
                <AsyncButton onClick={async () => { if (await confirm(`Delete "${c.name}"?`, { confirmLabel: "Delete" })) deleteMut.mutate(c.id); }} style={{ background: "none", border: "1px solid #fca5a5", borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontSize: 12, color: "#dc2626" }}>Delete</AsyncButton>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div ref={formRef} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, marginTop: 8 }}>
          <h4 style={{ margin: "0 0 12px", fontSize: 14, color: "#1e3a8a" }}>{editingId ? "Edit Client" : "New Client"}</h4>
          <ClientForm
            value={form}
            onChange={setForm}
            onSubmit={handleSubmit}
            onCancel={cancel}
            existingClients={clients as any[]}
            excludeId={editingId}
            isEditing={!!editingId}
            submitting={createMut.isPending || updateMut.isPending}
            taxSchemeOptions={Object.entries(CLIENT_TAX_SCHEMES).map(([value, label]) => ({ value, label }))}
            styles={{ label: labelStyle, input: inputStyle, submitButton: saveBtn }}
          />
        </div>
      )}

      {msg && <div style={{ fontSize: 12, color: "#16a34a", marginTop: 10 }}>{msg}</div>}
    </div>
  );
}

// ── Branding tab ─────────────────────────────────────────────────────────────

function BrandingTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: branding, isLoading } = useQuery({ queryKey: ["settings-branding"], queryFn: getSettingsBranding });
  // Recruitment intake config (jobs email, preferred candidate location, CV
  // limits) is deliberately absent — it lives in Recruiter Preferences now, with
  // the rest of the recruiting configuration and behind the Recruitment gate.
  const [form, setForm] = useState({ company_name: "", company_tagline: "", company_website: "", company_address: "", tax_number: "", company_mobile: "", signatory_name: "", signatory_designation: "", employee_id_prefix: "" });
  const [saved, setSaved] = useState(false);
  const [regenAllMsg, setRegenAllMsg] = useState("");
  const [logoKey, setLogoKey] = useState(Date.now());
  const [logoMsg, setLogoMsg] = useState("");
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [sigKey, setSigKey] = useState(Date.now());
  const [sigMsg, setSigMsg] = useState("");
  const [sigPreview, setSigPreview] = useState<string | null>(null);
  const [uploadingSig, setUploadingSig] = useState(false);

  React.useEffect(() => {
    if (branding) {
      setForm({
        company_name:          branding.company_name || "",
        company_tagline:       branding.company_tagline || "",
        company_website:       branding.company_website || "",
        company_address:       branding.company_address || "",
        tax_number:            branding.tax_number || "",
        company_mobile:        branding.company_mobile || "",
        signatory_name:        branding.signatory_name || "",
        signatory_designation: branding.signatory_designation || "",
        employee_id_prefix:    branding.employee_id_prefix || "",
      });
      if (branding.has_logo)      setLogoKey(Date.now());
      if (branding.has_signature) setSigKey(Date.now());
    }
  }, [branding]);

  const mutation = useMutation({
    mutationFn: saveBranding,
    onSuccess: () => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings-branding"] });
      qc.invalidateQueries({ queryKey: ["branding"] }); // nav/sidebar reads this key — refresh it so company name updates without a manual reload
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => toast(getErrorMessage(err, "Failed to save"), "error"),
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  // The signatory block prints only in the "Authorized by" section of quotation
  // and invoice PDFs, so it is Finance's, not Core's.
  const { owns, ownsAny } = useModules();
  const showSignatory = owns("finance");

  // Employee ID prefix change flow (spec employee-id-lifecycle AC7): dry-run first,
  // and when existing generated IDs would be affected, let the admin choose between
  // renumbering them or applying the prefix to future IDs only.
  const [prefixConfirm, setPrefixConfirm] = useState<null | { old_prefix: string; new_prefix: string; affected: number; examples: string[] }>(null);
  const [savingPrefix, setSavingPrefix] = useState(false);
  const prefixChanged = (form.employee_id_prefix || "") !== (branding?.employee_id_prefix || "");

  const handleSaveBranding = async () => {
    if (prefixChanged) {
      try {
        const dry = await api.post("/api/settings/employee-id-prefix", { prefix: form.employee_id_prefix, dry_run: true });
        if (dry.data.affected > 0) { setPrefixConfirm(dry.data); return; }
        await api.post("/api/settings/employee-id-prefix", { prefix: form.employee_id_prefix, apply_to_existing: false });
      } catch (err) {
        toast(getErrorMessage(err, "Failed to save Employee ID prefix"), "error");
        return;
      }
    }
    mutation.mutate(form);
  };

  const confirmPrefix = async (applyToExisting: boolean) => {
    setSavingPrefix(true);
    try {
      const r = await api.post("/api/settings/employee-id-prefix", { prefix: form.employee_id_prefix, apply_to_existing: applyToExisting });
      if (applyToExisting && r.data.rewritten > 0) {
        toast(`${r.data.rewritten} employee ID${r.data.rewritten === 1 ? "" : "s"} updated to ${r.data.new_prefix}-…`, "success");
        qc.invalidateQueries({ queryKey: ["employees"] });
      }
      setPrefixConfirm(null);
      mutation.mutate(form);
    } catch (err) {
      toast(getErrorMessage(err, "Failed to save Employee ID prefix"), "error");
    }
    setSavingPrefix(false);
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append("file", file);
    setUploadingLogo(true);
    try {
      await api.post("/api/settings/logo", fd);
      setLogoMsg("Logo uploaded!");
      setLogoKey(Date.now());
      qc.invalidateQueries({ queryKey: ["branding"] }); // nav/sidebar logo re-renders with a fresh cache-busted URL
      setTimeout(() => setLogoMsg(""), 3000);
    } catch {
      setLogoMsg("Upload failed. Use PNG or JPEG.");
    }
    setUploadingLogo(false);
  };

  const handleSigChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSigPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append("file", file);
    setUploadingSig(true);
    try {
      await api.post("/api/settings/signature", fd);
      setSigMsg("Signature uploaded!");
      setSigKey(Date.now());
      setTimeout(() => setSigMsg(""), 3000);
    } catch {
      setSigMsg("Upload failed. Use PNG or JPEG.");
    }
    setUploadingSig(false);
  };

  if (isLoading) return <div style={{ color: "#64748b", fontSize: 14 }}>Loading...</div>;

  const logoSrc = logoPreview || `${process.env.REACT_APP_API_URL || "http://localhost:30800"}/api/settings/logo?t=${logoKey}`;
  const sigSrc  = sigPreview  || `${process.env.REACT_APP_API_URL || "http://localhost:30800"}/api/settings/signature?t=${sigKey}`;

  return (
    <>
      <LoadingOverlay active={mutation.isPending} />
      <div style={card}>
        <h3 style={sectionHead}>Company Logo</h3>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
          Appears in the top-left of every formatted CV. PNG or JPEG recommended (square works best).
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ width: 80, height: 80, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img
              src={logoSrc}
              alt="Company logo"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
              onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
          </div>
          <div>
            <label style={{ ...saveBtn, display: "inline-block", cursor: uploadingLogo ? "not-allowed" : "pointer", opacity: uploadingLogo ? 0.6 : 1 }}>
              {uploadingLogo ? "Uploading…" : "Upload Logo"}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} style={{ display: "none" }} disabled={uploadingLogo} />
            </label>
            {logoMsg && <div style={{ fontSize: 12, color: logoMsg.includes("failed") ? "#dc2626" : "#16a34a", marginTop: 6 }}>{logoMsg}</div>}
          </div>
        </div>
      </div>

      {showSignatory && (
        <div style={card}>
          <h3 style={sectionHead}>Authorised Signatory Signature</h3>
          <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
            Appears in the "Authorized by" section of quotation and invoice PDFs. Upload a transparent PNG of the signature.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ width: 140, height: 60, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <img
                src={sigSrc}
                alt="Signature"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            </div>
            <div>
              <label style={{ ...saveBtn, display: "inline-block", cursor: uploadingSig ? "not-allowed" : "pointer", opacity: uploadingSig ? 0.6 : 1 }}>
                {uploadingSig ? "Uploading…" : "Upload Signature"}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleSigChange} style={{ display: "none" }} disabled={uploadingSig} />
              </label>
              {sigMsg && <div style={{ fontSize: 12, color: sigMsg.includes("failed") ? "#dc2626" : "#16a34a", marginTop: 6 }}>{sigMsg}</div>}
            </div>
          </div>
        </div>
      )}

      <div style={card}>
        <h3 style={sectionHead}>Company Details</h3>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
          These details appear on formatted CVs and in AI-generated job descriptions and assessments.
        </p>
        <div style={{ display: "grid", gap: 14 }}>
          <div>
            <label style={labelStyle}>Company Name *</label>
            <input value={form.company_name} onChange={set("company_name")} placeholder="Acme Staffing" style={inputStyle} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>This is the single source of truth for your company name — used company-wide across the sidebar, Workspace, CVs, invoices/quotations, and the careers page.</div>
          </div>
          <div>
            <label style={labelStyle}>Tagline</label>
            <input value={form.company_tagline} onChange={set("company_tagline")} placeholder="Talent Solutions" style={inputStyle} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Shown under company name on formatted CVs.</div>
          </div>
          <div>
            <label style={labelStyle}>Website</label>
            <input value={form.company_website} onChange={set("company_website")} placeholder="www.yourdomain.com" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Company Address</label>
            <input value={form.company_address} onChange={set("company_address")} placeholder="Full company address" style={inputStyle} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Printed on invoices and quotations.</div>
          </div>
          <div>
            <label style={labelStyle}>Tax Registration Number</label>
            <input value={form.tax_number} onChange={set("tax_number")} placeholder="e.g. GSTIN, VAT Reg No" style={inputStyle} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
              {ownsAny("finance", "payroll")
                ? "Shown on invoice/quotation PDFs. More tax fields (PAN, branch number) are under Settings → Locale → Tax Registration."
                : "Shown on payslips, HR letters and other company documents."}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Mobile / Phone</label>
            <input value={form.company_mobile} onChange={set("company_mobile")} placeholder="0855215415" style={inputStyle} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Shown on quotation and invoice PDFs.</div>
          </div>
          {showSignatory && (
            <>
              <div>
                <label style={labelStyle}>Authorised Signatory Name</label>
                <input value={form.signatory_name} onChange={set("signatory_name")} placeholder="e.g. John Smith" style={inputStyle} />
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>Printed below "Authorized by" on invoice and quotation PDFs.</div>
              </div>
              <div>
                <label style={labelStyle}>Signatory Designation</label>
                <input value={form.signatory_designation} onChange={set("signatory_designation")} placeholder="e.g. Managing Director" style={inputStyle} />
              </div>
            </>
          )}
          <div>
            <label style={labelStyle}>Employee ID Prefix</label>
            <input value={form.employee_id_prefix} onChange={set("employee_id_prefix")}
              placeholder="Auto (from company name)" style={inputStyle}
              onBlur={e => setForm(f => ({ ...f, employee_id_prefix: e.target.value.trim().toUpperCase() }))} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
              Used for auto-generated IDs, e.g. "ACME" → ACME-001. Leave blank to derive from company name.
            </div>
          </div>
        </div>
        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <button onClick={handleSaveBranding} disabled={mutation.isPending || savingPrefix} style={saveBtn}>
            {mutation.isPending || savingPrefix ? "Saving..." : "Save Branding"}
          </button>
          {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
        </div>
        {prefixConfirm && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div style={{ background: "#fff", borderRadius: 12, padding: 24, maxWidth: 480, width: "100%", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}>
              <h3 style={{ margin: "0 0 8px", fontSize: 16, color: "#0f172a" }}>Update existing employee IDs?</h3>
              <p style={{ fontSize: 13, color: "#475569", margin: "0 0 6px" }}>
                {prefixConfirm.affected} employee ID{prefixConfirm.affected === 1 ? "" : "s"} will change from{" "}
                <strong>{prefixConfirm.old_prefix}-…</strong> to <strong>{prefixConfirm.new_prefix}-…</strong> (numbers are kept
                {prefixConfirm.examples.length > 0 && <> — e.g. {prefixConfirm.examples[0]} → {prefixConfirm.new_prefix}-{prefixConfirm.examples[0].split("-").pop()}</>}).
              </p>
              <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 16px" }}>
                Manually assigned IDs are never changed. Choose "Future IDs only" to keep all current IDs as they are.
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button onClick={() => setPrefixConfirm(null)} disabled={savingPrefix}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 13, cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={() => confirmPrefix(false)} disabled={savingPrefix}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#0f172a", fontSize: 13, cursor: "pointer" }}>
                  Future IDs only
                </button>
                <button onClick={() => confirmPrefix(true)} disabled={savingPrefix}
                  style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#4f46e5", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  {savingPrefix ? "Updating..." : `Update ${prefixConfirm.affected} ID${prefixConfirm.affected === 1 ? "" : "s"}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Posts to /api/candidates/cv/regenerate-all, which is Recruitment-gated —
          without the module this card is a button that can only ever 403. */}
      {owns("recruitment") && (
      <div style={card}>
        <h3 style={sectionHead}>Regenerate All CVs</h3>
        <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
          Rebuilds formatted PDF and Word CVs for all candidates using your current branding (company name, logo, website, email).
          Run this once after updating your branding details above.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            disabled={!!regenAllMsg}
            onClick={() => {
              setRegenAllMsg("Queuing...");
              regenerateAllCVs()
                .then((r: any) => setRegenAllMsg(`✓ ${r.queued} CVs queued${r.skipped ? ` (${r.skipped} skipped — no raw file)` : ""}. Takes a few minutes.`))
                .catch(() => setRegenAllMsg("✗ Failed — check your role or try again."));
            }}
            style={{ ...saveBtn, background: "#7c3aed" }}
          >
            Regenerate All CVs
          </button>
          {regenAllMsg && <span style={{ fontSize: 13, color: regenAllMsg.startsWith("✓") ? "#16a34a" : regenAllMsg.startsWith("✗") ? "#dc2626" : "#64748b" }}>{regenAllMsg}</span>}
        </div>
      </div>
      )}

      {/* CV intake limits moved to Recruiter Preferences → Intake.
          Attendance geolocation / punch photos / timesheet moved to People
          Settings (/people-settings) — they are People-module config, not
          branding, and nobody could find them here. */}
    </>
  );
}

// ── Locale / Currency tab ────────────────────────────────────────────────────

function LocaleTab() {
  const qc = useQueryClient();
  const { data: locale } = useQuery({ queryKey: ["locale"], queryFn: getLocale });
  const { data: currencies = [] } = useQuery({ queryKey: ["currencies"], queryFn: getCurrencies });
  const [form, setForm] = useState({
    currency: "", currency_symbol: "", tax_rate: "", country: "",
    timezone: "", bank_name: "", bank_account: "", bank_branch: "",
    bank_swift: "", bank_branch_address: "", company_address: "",
    tax_label: "", tax_number: "", pan_number: "", branch_number: "",
  });
  const [saved, setSaved] = useState(false);

  React.useEffect(() => {
    if (locale) setForm({
      currency:        locale.currency || "USD",
      currency_symbol: locale.currency_symbol || "$",
      tax_rate:        locale.tax_rate || "0",
      country:         locale.country || "",
      timezone:        locale.timezone || "UTC",
      bank_name:       locale.bank_name || "",
      bank_account:    locale.bank_account || "",
      bank_branch:     locale.bank_branch || "",
      bank_swift:          locale.bank_swift || "",
      bank_branch_address: locale.bank_branch_address || "",
      company_address: locale.company_address || "",
      tax_label:       locale.tax_label || "",
      tax_number:      locale.tax_number || "",
      pan_number:      locale.pan_number || "",
      branch_number:   locale.branch_number || "",
    });
  }, [locale]);

  const mutation = useMutation({
    mutationFn: saveLocale,
    onSuccess: () => { setSaved(true); qc.invalidateQueries({ queryKey: ["locale"] }); setTimeout(() => setSaved(false), 3000); },
  });

  const onCurrencyChange = (code: string) => {
    const found = (currencies as any[]).find((c: any) => c.code === code);
    setForm(f => ({ ...f, currency: code, currency_symbol: found?.symbol || f.currency_symbol }));
  };

  // Currency, country and timezone are Core — every workspace needs them. The
  // rest of this tab is document configuration: bank details print on invoices
  // (Finance) and payslips (Payroll), and the tax fields configure sales
  // documents. Tax *number* is the exception and stays Core: it appears on
  // payslips and HR letters too, and the company_profile onboarding item
  // requires it.
  const { ownsAny } = useModules();
  const showBank = ownsAny("finance", "payroll");
  const showSalesTax = ownsAny("finance");

  return (
    <div style={card}>
      <LoadingOverlay active={mutation.isPending} />
      <h3 style={sectionHead}>Currency & Locale Settings</h3>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
        Configure the default currency used for salary slips and invoices. Each tenant has independent settings.
      </p>
      <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <label style={labelStyle}>Default Currency</label>
          <select value={form.currency} onChange={e => onCurrencyChange(e.target.value)}
            style={{ ...inputStyle, cursor: "pointer" }}>
            {(currencies as any[]).map((c: any) => (
              <option key={c.code} value={c.code}>{c.name} ({c.symbol})</option>
            ))}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              minWidth: 36, height: 28, padding: "0 10px",
              background: "#f1f5f9", border: "1.5px solid #e2e8f0", borderRadius: 7,
              fontSize: 16, fontWeight: 700, color: "#1e293b", letterSpacing: 0,
            }}>{form.currency_symbol}</span>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>Symbol — used on salary slips and invoices</span>
          </div>
        </div>
        {showSalesTax && (
          <div>
            <label style={labelStyle}>Default Tax Rate (%)</label>
            <input type="number" value={form.tax_rate} min={0} max={100} step="0.1"
              onChange={e => setForm(f => ({ ...f, tax_rate: e.target.value }))} style={inputStyle} />
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
              e.g. 7 for 7% VAT (Thailand). Applied to new invoices by default.
            </div>
          </div>
        )}
        <div>
          <label style={labelStyle}>Country</label>
          <select value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
            style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="">Select country…</option>
            {COUNTRY_LIST.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Company Timezone</label>
          <select value={form.timezone} onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}
            style={{ ...inputStyle, cursor: "pointer" }}>
            {TIMEZONE_LIST.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
          </select>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
            Used to display interview times in email notifications.
          </div>
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={labelStyle}>Company Address</label>
          <input value={form.company_address}
            onChange={e => setForm(f => ({ ...f, company_address: e.target.value }))}
            placeholder="Full company address" style={inputStyle} />
        </div>
      </div>

      {showBank && (
      <>
      <h3 style={{ ...sectionHead, marginTop: 24 }}>Bank / Payment Details</h3>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
        Printed on invoices and quotations in the Payment Details section.
      </p>
      <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div>
          <label style={labelStyle}>Bank Name</label>
          <input value={form.bank_name}
            onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
            placeholder="e.g. Bangkok Bank" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Account Number</label>
          <input value={form.bank_account}
            onChange={e => setForm(f => ({ ...f, bank_account: e.target.value }))}
            placeholder="Account / IBAN number" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Branch Name (optional)</label>
          <input value={form.bank_branch}
            onChange={e => setForm(f => ({ ...f, bank_branch: e.target.value }))}
            placeholder="e.g. Erawan Tower Branch" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>SWIFT / BIC Code (optional)</label>
          <input value={form.bank_swift}
            onChange={e => setForm(f => ({ ...f, bank_swift: e.target.value.toUpperCase() }))}
            placeholder="e.g. BKKBTHBK" style={inputStyle} />
        </div>
        <div style={{ gridColumn: "span 2" }}>
          <label style={labelStyle}>Branch Address (optional)</label>
          <input value={form.bank_branch_address}
            onChange={e => setForm(f => ({ ...f, bank_branch_address: e.target.value }))}
            placeholder="Branch's mailing address" style={inputStyle} />
        </div>
      </div>
      </>
      )}

      <h3 style={{ ...sectionHead, marginTop: 24 }}>Tax Registration</h3>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
        {showSalesTax
          ? "Shown on invoice/quotation PDFs. Examples: GSTIN (India), VAT Reg No (Thailand)."
          : "Your company's tax registration number, shown on payslips and HR letters."}
      </p>
      <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {showSalesTax && (
          <div>
            <label style={labelStyle}>Tax Label</label>
            <input value={form.tax_label} onChange={e => setForm(f => ({ ...f, tax_label: e.target.value }))}
              placeholder="e.g. GSTIN, VAT Reg No" style={inputStyle} />
          </div>
        )}
        <div>
          <label style={labelStyle}>Tax Registration Number</label>
          <input value={form.tax_number} onChange={e => setForm(f => ({ ...f, tax_number: e.target.value }))}
            placeholder="e.g. 29ABCDE1234F1Z5" style={inputStyle} />
        </div>
        {showBank && (
          <div>
            <label style={labelStyle}>PAN Number <span style={{ color: "#94a3b8", fontWeight: 400 }}>(India)</span></label>
            <input value={form.pan_number} onChange={e => setForm(f => ({ ...f, pan_number: e.target.value }))}
              placeholder="e.g. ABCDE1234F" style={inputStyle} />
          </div>
        )}
        {showSalesTax && (
          <div>
            <label style={labelStyle}>Branch Number <span style={{ color: "#94a3b8", fontWeight: 400 }}>(Thailand)</span></label>
            <input value={form.branch_number} onChange={e => setForm(f => ({ ...f, branch_number: e.target.value }))}
              placeholder="e.g. 00000 (HQ)" style={inputStyle} />
          </div>
        )}
      </div>
      <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending} style={saveBtn}>
          {mutation.isPending ? "Saving..." : "Save Locale Settings"}
        </button>
        {saved && <span style={{ fontSize: 13, color: "#16a34a" }}>Saved!</span>}
      </div>
    </div>
  );
}

// ── Shared components ────────────────────────────────────────────────────────

function StepHeader({ n, title, done }: { n: number; title: string; done: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: done ? TEAL : NAVY, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
        {done ? "✓" : n}
      </div>
      <h3 style={{ ...sectionHead, margin: 0, border: "none", paddingBottom: 0 }}>{title}</h3>
    </div>
  );
}

function Field({ label, placeholder, value, onChange, type = "text" }: { label: string; placeholder?: string; value: string; onChange: any; type?: string }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type={type} placeholder={placeholder} value={value} onChange={onChange} style={inputStyle} />
    </div>
  );
}

const card: React.CSSProperties = { background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" };
const sectionHead: React.CSSProperties = { color: NAVY, fontSize: 15, margin: "0 0 16px", borderBottom: `2px solid ${TEAL}`, paddingBottom: 6 };
const labelStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 };
const inputStyle: React.CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box" };
const saveBtn: React.CSSProperties = { background: NAVY, color: "white", border: "none", padding: "9px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const providerCard: React.CSSProperties = { flex: 1, borderRadius: 10, padding: "16px 20px", textAlign: "center", transition: "all 0.15s" };
const link: React.CSSProperties = { color: "#1d4ed8" };
const code: React.CSSProperties = { background: "#f1f5f9", padding: "1px 5px", borderRadius: 4, fontSize: 12, fontFamily: "monospace", color: NAVY };
